import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import {
  ArrowLeft,
  Calculator,
  CheckCircle2,
  Flame,
  Lightbulb,
  Snowflake,
  Thermometer,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  CHANGE_TYPE_LABELS,
  DIRECTION_LABELS,
  ENERGY_CALORIMETRY_TOOL_IDENTITY,
  ENERGY_MODES,
  ENERGY_SOURCES,
  EVIDENCE_CASES,
  MODEL_BOUNDARIES,
  STANDARD_CONDITIONS,
  checkEvidencePrediction,
  formatDecimalSigned,
  temperatureChange,
  type ChangeType,
  type EnergyDirection,
  type EnergyMode,
  type EvidenceCase,
} from './energyCalorimetry';
import { EnergyPacksExplore } from './EnergyPacksExplore';
import { SeniorEnergyMode } from './EnergyCalorimetrySeniorMode';
import { EnergyLevelDiagram, ThermometerReading } from './EnergyCalorimetryVisuals';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  EnergyMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  evidence: {
    tab: 'Year 8 · Evidence',
    eyebrow: 'Energy changes',
    title: 'Does it release energy or take energy in?',
    description:
      'Explore: make a hot pack or a cold pack and watch the thermometer. Practice: predict, then check the evidence.',
  },
  senior: {
    tab: 'Senior · Thermochemistry',
    eyebrow: 'Enthalpy and calorimetry',
    title: 'Measure, calculate and combine enthalpy changes',
    description:
      'Use q = mcΔT and ΔH = −q/n, draw energy profiles, apply Hess’s law, compare fuels and test spontaneity with ΔG = ΔH − TΔS.',
  },
};

const DIRECTION_OPTIONS: EnergyDirection[] = ['exothermic', 'endothermic'];

type Year8View = 'explore' | 'practice';
const YEAR8_VIEWS: { id: Year8View; label: string }[] = [
  { id: 'explore', label: 'Explore' },
  { id: 'practice', label: 'Practice' },
];

/** Year 8: Explore (hot and cold pack lab, own inputs) first, then Practice (the evidence quiz). */
function Year8EnergyMode() {
  const [view, setView] = useState<Year8View>('explore');
  useScienceTutorSection('Year8EnergyMode', { view });
  return (
    <div className="space-y-4">
      <div className="inline-grid grid-cols-2 gap-1 rounded-full bg-surface p-1" aria-label="Year 8 view">
        {YEAR8_VIEWS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={view === entry.id}
            onClick={() => setView(entry.id)}
            className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === entry.id ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {entry.label}
          </button>
        ))}
      </div>
      {view === 'explore' ? <EnergyPacksExplore /> : <EvidenceMode />}
    </div>
  );
}
const CHANGE_TYPE_OPTIONS: ChangeType[] = ['chemical', 'physical'];

function EvidenceReveal({ entry }: { entry: EvidenceCase }) {
  const { evidence } = entry;
  if (evidence.kind === 'energy-input') {
    return (
      <div className="grid gap-3">
        <div className="rounded-2xl border border-brand-sunshine/40 bg-wash-sunshine p-4">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <Flame className="h-4 w-4 text-brand-coral" aria-hidden="true" /> Energy supplied by{' '}
            {evidence.source}
          </p>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">{evidence.observation}</p>
        </div>
        <div className="rounded-2xl border border-hairline bg-surface p-4">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <TriangleAlert className="h-4 w-4 text-brand-coral" aria-hidden="true" /> Thermometer
            trap
          </p>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">
            {evidence.thermometerTrap}
          </p>
        </div>
      </div>
    );
  }
  const change = temperatureChange(entry) ?? 0;
  const tone = change > 0 ? 'warmer' : 'cooler';
  return (
    <div className="rounded-2xl border border-hairline bg-surface p-4">
      <p className="text-sm font-bold text-slate2">Thermometer in {evidence.location}</p>
      <div className="mt-3 flex flex-wrap items-end justify-center gap-6 sm:gap-10">
        <ThermometerReading label="Before" temperatureC={evidence.startC} tone="start" />
        <ThermometerReading label="After" temperatureC={evidence.endC} tone={tone} />
        {evidence.control ? (
          <ThermometerReading
            label={`Control: ${evidence.control.label}`}
            temperatureC={evidence.control.endC}
            tone="start"
          />
        ) : null}
      </div>
      <p className="mt-4 flex items-center justify-center gap-2 text-center text-base font-extrabold text-ink">
        {change > 0 ? (
          <Flame className="h-5 w-5 text-brand-coral" aria-hidden="true" />
        ) : (
          <Snowflake className="h-5 w-5 text-brand-sky" aria-hidden="true" />
        )}
        ΔT = {formatDecimalSigned(change, 1)} °C ({change > 0 ? 'temperature rose' : 'temperature fell'})
      </p>
    </div>
  );
}

function EvidenceMode() {
  const [selected, setSelected] = useState<EvidenceCase>(EVIDENCE_CASES[0]);
  const [direction, setDirection] = useState<EnergyDirection | null>(null);
  const [changeType, setChangeType] = useState<ChangeType | null>(null);
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('EvidenceMode', { selected: tutorSelection(selected), direction, changeType, revealed });
  const check =
    revealed && direction && changeType
      ? checkEvidencePrediction(selected, direction, changeType)
      : null;

  const choose = (entry: EvidenceCase) => {
    setSelected(entry);
    setDirection(null);
    setChangeType(null);
    setRevealed(false);
  };

  const startReading =
    selected.evidence.kind === 'thermometer'
      ? `Starting temperature: ${selected.evidence.startC.toFixed(1)} °C`
      : `Energy source: ${selected.evidence.source}`;

  return (
    <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Choose a change
        </p>
        <div className="mt-3 grid gap-2" aria-label="Reactions and processes">
          {EVIDENCE_CASES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={selected.id === entry.id}
              onClick={() => choose(entry)}
              className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold ${selected.id === entry.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
            >
              {entry.name}
            </button>
          ))}
        </div>
      </aside>

      <section className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <h3 className="text-xl font-extrabold text-ink">{selected.name}</h3>
        <p className="mt-2 break-words font-mono text-sm font-bold text-ink">{selected.equation}</p>
        <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-surface px-3 py-1 text-xs font-extrabold text-slate2">
          <Thermometer className="h-4 w-4" aria-hidden="true" /> {startReading}
        </p>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">1. Predict the energy change</legend>
            <div className="mt-2 grid gap-2">
              {DIRECTION_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={direction === option}
                  onClick={() => {
                    setDirection(option);
                    setRevealed(false);
                  }}
                  className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold ${direction === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
                >
                  {DIRECTION_LABELS[option]}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">2. What kind of change is it?</legend>
            <div className="mt-2 grid gap-2">
              {CHANGE_TYPE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={changeType === option}
                  onClick={() => {
                    setChangeType(option);
                    setRevealed(false);
                  }}
                  className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold ${changeType === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
                >
                  {CHANGE_TYPE_LABELS[option]}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <button
          type="button"
          disabled={!direction || !changeType}
          onClick={() => setRevealed(true)}
          className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reveal the evidence
        </button>

        {revealed && check ? (
          <div className="mt-6 grid gap-4">
            <EvidenceReveal entry={selected} />
            <p className="text-sm font-medium leading-6 text-slate2">
              <strong className="text-ink">Observation:</strong> {selected.observation}
            </p>
            <div
              role="status"
              className={`rounded-2xl border p-4 ${check.directionCorrect && check.changeTypeCorrect ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
            >
              <p className="flex items-center gap-2 font-extrabold text-ink">
                {check.directionCorrect && check.changeTypeCorrect ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-mint" aria-hidden="true" />
                ) : (
                  <Lightbulb className="h-5 w-5 shrink-0 text-brand-coral" aria-hidden="true" />
                )}
                {check.directionCorrect
                  ? `Correct prediction: ${selected.direction}.`
                  : `The evidence shows ${selected.direction}, not ${direction}.`}
              </p>
              <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                {check.directionFeedback}
              </p>
              <p className="mt-3 text-sm font-extrabold text-ink">
                {check.changeTypeCorrect
                  ? 'Change type: correct.'
                  : `Change type: this is a ${selected.changeType} change, not ${changeType}.`}
              </p>
              <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                {check.changeTypeFeedback}
              </p>
            </div>
            <EnergyLevelDiagram direction={selected.direction} />
          </div>
        ) : null}
        <p className="mt-4 text-xs font-medium leading-5 text-slate2">
          {ENERGY_SOURCES.model.detail} Dissolving is shown as a physical change because the
          solute can be recovered; it still has a real energy change.
        </p>
      </section>
    </div>
  );
}

export function EnergyCalorimetryLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<EnergyMode>(() => modeForYear(ENERGY_MODES, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'energy-calorimetry', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-coral/25 bg-gradient-to-br from-wash-coral via-canvas-pure to-wash-sky p-6 shadow-card-soft lg:p-8"
        data-tool-key={ENERGY_CALORIMETRY_TOOL_IDENTITY.toolKey}
        data-tool-version={ENERGY_CALORIMETRY_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-coral">
              <Thermometer className="h-4 w-4" aria-hidden="true" /> Energy and thermochemistry
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Energy &amp; Calorimetry Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Follow energy from a thermometer reading to enthalpy changes, energy profiles,
              Hess’s law and fuel choices.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 8–12 · progressive model
          </div>
        </div>

        <div
          className="mt-6 grid max-w-xl grid-cols-2 rounded-full bg-white/75 p-1"
          aria-label="Choose learning level"
        >
          {ENERGY_MODES.map(({ mode: option }) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              {copy.eyebrow}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            {mode === 'evidence' ? (
              <Thermometer className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Calculator className="h-4 w-4" aria-hidden="true" />
            )}
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'evidence' ? <Year8EnergyMode /> : <SeniorEnergyMode />}

      <section
        className="mt-6 rounded-3xl border border-brand-sunshine/40 bg-wash-sunshine p-5"
        aria-label="Model boundaries"
      >
        <p className="text-sm font-extrabold text-ink">Model boundaries</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
          {MODEL_BOUNDARIES.map((boundary) => (
            <li key={boundary}>{boundary}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs font-bold leading-5 text-slate2">
          {STANDARD_CONDITIONS} Sources: {ENERGY_SOURCES.nist.label}; {ENERGY_SOURCES.crc.label};{' '}
          {ENERGY_SOURCES.derived.label}. Reviewed values are pending teaching review.
        </p>
      </section>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases. It does not replace a supervised practical,
        determine laboratory safety, save an assessment attempt or claim alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
